import { useEffect, useRef, useState } from "react";
import {
  ADCASH_SIDE_BANNERS_ENABLED,
  ADCASH_SIDE_ZONES,
} from "../config/ads.js";
import {
  canLoadAdcash,
  loadAdcashAutotag,
  renderAdcashBanner,
} from "../services/adcash.js";

const SIDE_AD_MEDIA_QUERY = "(min-width: 1540px) and (min-height: 680px)";
const BANNER_GAP_MS = 700;

const wait = (milliseconds) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

function useSideAdViewport() {
  const [matches, setMatches] = useState(
    () => window.matchMedia(SIDE_AD_MEDIA_QUERY).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(SIDE_AD_MEDIA_QUERY);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return matches;
}

function SideAd({ containerRef, position, zone }) {
  return (
    <aside
      aria-label={`Publicidade lateral ${position === "left" ? "esquerda" : "direita"}`}
      className={`side-ad side-ad--${position}`}
      style={{
        "--ad-width": `${zone.width}px`,
        "--ad-height": `${zone.height}px`,
      }}
    >
      <span>Publicidade</span>
      <div
        className="side-ad-slot"
        data-adcash-zone={zone.zoneId}
        ref={containerRef}
      />
    </aside>
  );
}

export function SideAds() {
  const fitsViewport = useSideAdViewport();
  const leftContainerRef = useRef(null);
  const rightContainerRef = useRef(null);

  useEffect(() => {
    let active = true;

    const startAds = async () => {
      try {
        await loadAdcashAutotag();
        if (
          !active ||
          !ADCASH_SIDE_BANNERS_ENABLED ||
          !canLoadAdcash() ||
          !fitsViewport
        ) {
          return;
        }

        await wait(BANNER_GAP_MS);
        if (!active) return;
        await renderAdcashBanner(
          leftContainerRef.current,
          ADCASH_SIDE_ZONES.left.zoneId,
        );
        await wait(BANNER_GAP_MS);
        if (!active) return;
        await renderAdcashBanner(
          rightContainerRef.current,
          ADCASH_SIDE_ZONES.right.zoneId,
        );
      } catch (error) {
        if (active) {
          console.error("Não foi possível iniciar os anúncios Adcash:", error);
        }
      }
    };

    startAds();
    return () => {
      active = false;
    };
  }, [fitsViewport]);

  if (!ADCASH_SIDE_BANNERS_ENABLED || !canLoadAdcash() || !fitsViewport) {
    return null;
  }

  return (
    <>
      <SideAd
        containerRef={leftContainerRef}
        position="left"
        zone={ADCASH_SIDE_ZONES.left}
      />
      <SideAd
        containerRef={rightContainerRef}
        position="right"
        zone={ADCASH_SIDE_ZONES.right}
      />
    </>
  );
}
